// Protocolo do Hábito — a medida de automaticidade (SRBAI) e a formatura.
//
// Dias consecutivos medem o uso do aplicativo. O que mede o HÁBITO são estas
// quatro perguntas, que têm correlação de 0,92 com a escala completa de doze
// itens. É a diferença entre o app achar que você formou um hábito e o app
// saber.
//
// A formatura segue uma ordem que não é estética: "após entregar um marco, a
// atividade cai — o próximo objetivo precisa estar encadeado ANTES da entrega
// do troféu". Por isso a sequência é números → veredito honesto → o que vem
// agora → e só então o selo.

import React, { useState } from 'react';
import { Icon } from '../icons';
import { AuthSession } from '../../types';
import { EstadoHabito, habitoApi } from './api';

const serif = 'Instrument Serif, Georgia, serif';
const CREME = '#F5F1EA';

const eyebrow: React.CSSProperties = {
  fontSize: 10.5, letterSpacing: '0.16em', textTransform: 'uppercase', fontWeight: 600,
};

const PERGUNTAS = [
  { id: 'q1', texto: 'faço automaticamente' },
  { id: 'q2', texto: 'faço sem precisar lembrar conscientemente' },
  { id: 'q3', texto: 'faço sem pensar' },
  { id: 'q4', texto: 'começo antes de perceber que comecei' },
] as const;

/* ── As quatro perguntas ─────────────────────────────────────────────── */
export const Srbai: React.FC<{
  session: AuthSession | null;
  onPronto: (score: number) => void;
  onAdiar?: () => void;
}> = ({ session, onPronto, onAdiar }) => {
  const [r, setR] = useState<Record<string, number>>({});
  const [salvando, setSalvando] = useState(false);
  const completo = PERGUNTAS.every((q) => r[q.id]);

  async function enviar() {
    setSalvando(true);
    try {
      const res: any = await habitoApi.responderSrbai(session, {
        q1: r.q1, q2: r.q2, q3: r.q3, q4: r.q4,
      });
      onPronto(Number(res?.medicao?.score) || 0);
    } finally { setSalvando(false); }
  }

  return (
    <div style={{ padding: '0 4px' }}>
      <div className="mono" style={{ ...eyebrow, color: 'rgba(245,241,234,0.45)' }}>A medida</div>
      <h2 style={{ fontFamily: serif, fontSize: 30, lineHeight: 1.12, color: CREME, margin: '10px 0 8px' }}>
        Ler é algo que <i>eu…</i>
      </h2>
      <p style={{ fontSize: 14, lineHeight: 1.55, color: 'rgba(245,241,234,0.5)', margin: '0 0 22px' }}>
        Quatro perguntas, trinta segundos. É assim que dá para dizer se o hábito
        pegou de verdade — em vez de deduzir por dias seguidos.
      </p>

      {PERGUNTAS.map((q, i) => (
        <div key={q.id} style={{ marginBottom: 22 }}>
          <div style={{ display: 'flex', gap: 9, alignItems: 'baseline', marginBottom: 10 }}>
            <span className="mono" style={{ fontSize: 10, color: 'var(--ember)' }}>0{i + 1}</span>
            <span style={{ fontSize: 16, color: CREME, lineHeight: 1.35 }}>{q.texto}</span>
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            {[1, 2, 3, 4, 5, 6, 7].map((n) => {
              const on = r[q.id] === n;
              return (
                <button key={n} onClick={() => setR((v) => ({ ...v, [q.id]: n }))} style={{
                  flex: 1, height: 42, borderRadius: 12, cursor: 'pointer',
                  border: `1px solid ${on ? 'var(--ember)' : 'rgba(245,241,234,0.14)'}`,
                  background: on ? 'var(--ember)' : 'transparent',
                  color: on ? '#fff' : 'rgba(245,241,234,0.55)',
                  fontSize: 14, fontWeight: on ? 700 : 500, transition: 'all .15s',
                }}>{n}</button>
              );
            })}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6, fontSize: 10.5, color: 'rgba(245,241,234,0.32)' }}>
            <span>discordo totalmente</span><span>concordo totalmente</span>
          </div>
        </div>
      ))}

      <button onClick={enviar} disabled={!completo || salvando} style={{
        width: '100%', marginTop: 6, padding: '16px 20px', borderRadius: 999, border: 'none',
        background: completo ? 'var(--ember)' : 'rgba(245,241,234,0.10)',
        color: completo ? '#fff' : 'rgba(245,241,234,0.35)',
        fontSize: 15.5, fontWeight: 600, cursor: completo ? 'pointer' : 'default',
      }}>
        {salvando ? 'um instante…' : completo ? 'Ver o resultado' : 'Responda as quatro'}
      </button>
      {onAdiar && (
        <button onClick={onAdiar} style={{
          width: '100%', background: 'none', border: 'none', color: 'rgba(245,241,234,0.4)',
          fontSize: 13.5, padding: '13px 0 0', cursor: 'pointer',
        }}>Depois</button>
      )}
    </div>
  );
};

/* ── O veredito, sem maquiagem ───────────────────────────────────────── */
const Veredito: React.FC<{ score: number }> = ({ score }) => {
  const alta = score >= 5.5;
  const media = score >= 4 && score < 5.5;
  return (
    <div style={{
      background: 'rgba(245,241,234,0.05)', border: '1px solid rgba(245,241,234,0.10)',
      borderRadius: 20, padding: 20, marginBottom: 16,
    }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 10 }}>
        <div style={{ fontFamily: serif, fontSize: 40, color: 'var(--ember)', lineHeight: 1 }}>
          {score.toFixed(1).replace('.', ',')}
        </div>
        <div style={{ fontSize: 13, color: 'rgba(245,241,234,0.5)' }}>de 7</div>
      </div>
      <div style={{ fontFamily: serif, fontSize: 23, color: CREME, marginBottom: 8 }}>
        {alta ? 'Ler já acontece sozinho.' : media ? 'Está a caminho.' : 'Ainda não virou automático.'}
      </div>
      <p style={{ fontSize: 14, lineHeight: 1.6, color: 'rgba(245,241,234,0.7)', margin: 0 }}>
        {alta
          ? 'Pelas quatro perguntas, ler já está funcionando como resposta ao contexto, e não como decisão. Foi exatamente isso que a gente veio buscar.'
          : media
            ? 'Você já lê sem pensar em boa parte dos dias, mas ainda decide em alguns. É um lugar normal de se estar no dia 67 — a mediana dos estudos é entre 59 e 78 dias, e essa é só a mediana.'
            : 'E isso não é fracasso: metade das pessoas do estudo original também não tinha chegado lá em 84 dias, e a faixa normal vai até 254 dias. O que muda daqui pra frente é só o tempo.'}
      </p>
      <p style={{ fontSize: 11.5, lineHeight: 1.5, color: 'rgba(245,241,234,0.35)', margin: '12px 0 0' }}>
        As faixas de leitura acima são interpretação nossa: a escala é validada, mas
        não existe ponto de corte publicado para "formou o hábito".
      </p>
    </div>
  );
};

/* ── A formatura ─────────────────────────────────────────────────────── */
export const Formatura: React.FC<{
  session: AuthSession | null;
  estado: EstadoHabito;
  onConcluir: () => void;
  onAdiar: () => void;
  onEditarPlano?: () => void;
  onEscolherLivro?: () => void;
}> = ({ session, estado, onConcluir, onAdiar, onEditarPlano, onEscolherLivro }) => {
  const jaMediu = !!estado.medicao?.ultimo && (estado.medicao.ultimo.diaPrograma >= (estado.diaMarco || 67));
  const [fase, setFase] = useState<'medir' | 'retrato' | 'proximo' | 'selo'>(jaMediu ? 'retrato' : 'medir');
  const [score, setScore] = useState<number>(estado.medicao?.ultimo?.score || 0);
  type Proximo = 'manutencao' | 'novo-livro' | 'mais-paginas' | 'trocar-plano';
  const [proximo, setProximo] = useState<Proximo | null>(null);
  const [salvando, setSalvando] = useState(false);

  // Chegar ao dia 67 do calendário não é o mesmo que ter feito o programa. O
  // servidor separa os dois casos (limiar de 4 leituras por semana OU
  // automaticidade medida); a tela precisa parar de dar o mesmo selo aos dois.
  // Dizer "você é alguém que lê" para quem leu 5 dias em 67 é a única mentira
  // que o programa inteiro poderia contar — e seria a última coisa que a pessoa
  // ouviria dele.
  const plena = (estado.formaturaTipo || 'plena') === 'plena';

  async function fechar() {
    if (!proximo) return;
    setSalvando(true);
    try {
      // "Trocar o plano" não é um destino no servidor: ela fecha o programa em
      // manutenção e o editor abre por cima. O que muda é o plano, não o estado.
      await habitoApi.formar(session, proximo === 'trocar-plano' ? 'manutencao' : proximo);
      setFase('selo');
    } finally { setSalvando(false); }
  }

  const seq = estado.sequencia;

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9660, background: '#141110',
      display: 'flex', flexDirection: 'column', maxWidth: 460, margin: '0 auto',
      overflowY: 'auto', animation: 'e2FadeUp .32s ease both',
    }}>
      <style>{`@keyframes habSelo2 { 0% { transform: scale(1.5) rotate(-8deg); opacity: 0 } 60% { transform: scale(.97) rotate(2deg); opacity: 1 } 100% { transform: scale(1); opacity: 1 } }`}</style>

      {/* A SAÍDA. Ela existia só na fase de medir — e como `jaMediu` fica
          verdadeiro para sempre depois da primeira resposta, quem respondia as
          quatro perguntas no dia 67 ficava TRANCADO num overlay de tela cheia
          sem nenhum jeito de voltar para a Home a não ser se formando. Fechar o
          app não resolvia: o adiamento só era gravado dentro do `onAdiar`. */}
      {fase !== 'selo' && (
        <button onClick={onAdiar} aria-label="Fechar" style={{
          position: 'absolute', top: 'calc(16px + env(safe-area-inset-top,0px))', right: 18, zIndex: 2,
          width: 36, height: 36, borderRadius: '50%', border: 'none', cursor: 'pointer',
          background: 'rgba(245,241,234,0.08)', color: 'rgba(245,241,234,0.6)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Icon name="close" size={16} color="rgba(245,241,234,0.6)" />
        </button>
      )}

      <div style={{ flex: 1, padding: 'calc(28px + env(safe-area-inset-top,0px)) 24px 24px' }}>

        {fase === 'medir' && (
          <Srbai session={session} onAdiar={onAdiar} onPronto={(s) => { setScore(s); setFase('retrato'); }} />
        )}

        {fase === 'retrato' && (<>
          <div className="mono" style={{ ...eyebrow, color: 'rgba(245,241,234,0.45)' }}>
            O marco
          </div>
          {/* O título dizia sempre "dia 67", mesmo para quem chega aqui no dia
              150 por ter adiado. O marco é 67; o dia dela é o dela. */}
          <h2 style={{ fontFamily: serif, fontSize: 34, lineHeight: 1.08, color: CREME, margin: '10px 0 18px' }}>
            Você chegou<br />ao <i>dia {estado.dia && estado.dia > (estado.diaMarco || 67) ? estado.dia : estado.diaMarco}</i>.
          </h2>
          {!plena && (
            <p style={{ fontSize: 14.5, lineHeight: 1.6, color: 'rgba(245,241,234,0.6)', margin: '-6px 0 18px' }}>
              O calendário chegou aqui; a dose ainda não. Isso não anula nada do que
              está abaixo — só quer dizer que o trabalho continua, e eu prefiro te
              dizer isso a te dar um diploma que você não pediu.
            </p>
          )}

          <div style={{ display: 'flex', gap: 24, marginBottom: 20, flexWrap: 'wrap' }}>
            {[
              { n: seq.diasLidos, r: seq.diasLidos === 1 ? 'dia lido' : 'dias lidos' },
              { n: seq.melhor, r: 'melhor sequência' },
              { n: estado.dia || 0, r: 'dias de programa' },
            ].map((x) => (
              <div key={x.r}>
                <div style={{ fontFamily: serif, fontSize: 34, lineHeight: 1, color: CREME }}>{x.n}</div>
                <div className="mono" style={{ ...eyebrow, fontSize: 9.5, color: 'rgba(245,241,234,0.42)', marginTop: 4 }}>{x.r}</div>
              </div>
            ))}
          </div>

          <Veredito score={score} />

          <button onClick={() => setFase('proximo')} style={{
            width: '100%', padding: '16px 20px', borderRadius: 999, border: 'none',
            background: 'var(--ember)', color: '#fff', fontSize: 15.5, fontWeight: 600, cursor: 'pointer',
          }}>
            E agora?
          </button>
        </>)}

        {/* O próximo objetivo vem ANTES do troféu, de propósito. */}
        {fase === 'proximo' && (<>
          <div className="mono" style={{ ...eyebrow, color: 'rgba(245,241,234,0.45)' }}>Antes de fechar</div>
          <h2 style={{ fontFamily: serif, fontSize: 31, lineHeight: 1.1, color: CREME, margin: '10px 0 8px' }}>
            O que vem <i>agora</i>?
          </h2>
          <p style={{ fontSize: 14.5, lineHeight: 1.6, color: 'rgba(245,241,234,0.6)', margin: '0 0 20px' }}>
            Depois de um marco entregue, a atividade cai — em qualquer programa, com
            qualquer pessoa. Por isso eu não fecho isto aqui sem você escolher o
            próximo passo. Nenhum deles aperta o ritmo se você não quiser.
          </p>

          {/* Para quem não bateu a dose, "subir o mínimo" é a sugestão errada: o
              que faltou foi frequência, não volume. No lugar dela entra a única
              alavanca que a evidência aponta — o plano, que talvez nunca tenha
              cabido na semana dessa pessoa. */}
          {[
            { id: 'manutencao', t: 'Manter exatamente como está', d: 'mesmo contrato, mesmo mínimo. Sem contador de programa, sem cobrança.' },
            { id: 'novo-livro', t: 'Escolher o próximo livro', d: 'mantendo o contrato. Duas ou três opções, você decide.' },
            plena
              ? { id: 'mais-paginas', t: 'Subir um pouco o mínimo', d: 'de 1 para 5 páginas. Só se você quiser — o mínimo pequeno é o que fez funcionar.' }
              : { id: 'trocar-plano', t: 'Trocar o horário e o lugar', d: 'plano que não coube na semana não é falta de disciplina. Abro o editor em seguida.' },
          ].map((o) => {
            const on = proximo === o.id;
            return (
              <button key={o.id} onClick={() => setProximo(o.id as any)} style={{
                display: 'block', width: '100%', textAlign: 'left', padding: '15px 17px',
                borderRadius: 18, marginBottom: 10, cursor: 'pointer',
                background: on ? 'rgba(232,122,59,0.14)' : 'rgba(245,241,234,0.04)',
                border: `1px solid ${on ? 'var(--ember)' : 'rgba(245,241,234,0.10)'}`,
              }}>
                <div style={{ fontFamily: serif, fontSize: 19, color: CREME }}>{o.t}</div>
                <div style={{ fontSize: 13, color: 'rgba(245,241,234,0.5)', marginTop: 3, lineHeight: 1.45 }}>{o.d}</div>
              </button>
            );
          })}

          <button onClick={fechar} disabled={!proximo || salvando} style={{
            width: '100%', marginTop: 8, padding: '16px 20px', borderRadius: 999, border: 'none',
            background: proximo ? 'var(--ember)' : 'rgba(245,241,234,0.10)',
            color: proximo ? '#fff' : 'rgba(245,241,234,0.35)',
            fontSize: 15.5, fontWeight: 600, cursor: proximo ? 'pointer' : 'default',
          }}>
            {salvando ? 'um instante…' : 'É esse o próximo passo'}
          </button>
        </>)}

        {fase === 'selo' && (
          <div style={{ textAlign: 'center', paddingTop: 40 }}>
            <div style={{
              width: 92, height: 92, borderRadius: '50%', margin: '0 auto 24px',
              background: 'var(--ember)', display: 'grid', placeItems: 'center',
              boxShadow: '0 18px 44px -14px var(--ember)',
              animation: 'habSelo2 .7s cubic-bezier(.2,.8,.2,1) both',
            }}>
              <Icon name={plena ? 'flame' : 'book'} size={44} color="#fff" />
            </div>
            <div className="mono" style={{ ...eyebrow, color: 'rgba(245,241,234,0.45)' }}>
              {plena ? 'Programa concluído' : 'O programa fecha aqui'}
            </div>
            <h2 style={{ fontFamily: serif, fontSize: 36, lineHeight: 1.08, color: CREME, margin: '12px 0 14px' }}>
              {plena
                ? <>Você é alguém<br />que <i>lê</i>.</>
                : <>O contador fecha.<br />O <i>livro</i> não.</>}
            </h2>
            <p style={{ fontSize: 15, lineHeight: 1.62, color: 'rgba(245,241,234,0.65)', maxWidth: 320, margin: '0 auto 26px' }}>
              {plena
                ? 'O contador some daqui pra frente. O contrato fica. Eu vou aparecer bem menos — e é isso que a gente combinou desde o começo.'
                : 'Eu não vou te dizer que você formou um hábito, porque os dias não dizem isso. O que eu posso dizer é que o plano continua de pé e a porta fica aberta — a faixa normal de formação vai até 254 dias, e ninguém está contando os seus.'}
            </p>
            <button
              onClick={() => {
                onConcluir();
                if (proximo === 'trocar-plano') onEditarPlano?.();
                // "Escolher o próximo livro" não escolhia nada: fechava o selo e
                // voltava para a Home com o mesmo livro de sempre.
                if (proximo === 'novo-livro') onEscolherLivro?.();
              }}
              style={{
                width: '100%', padding: '16px 20px', borderRadius: 999, border: 'none',
                background: 'var(--ember)', color: '#fff', fontSize: 15.5, fontWeight: 600, cursor: 'pointer',
              }}
            >
              {proximo === 'trocar-plano' ? 'Ajustar o plano' : proximo === 'novo-livro' ? 'Escolher o livro' : 'Continuar lendo'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

/* ── A medição periódica, fora da formatura ──────────────────────────── */
export const MedicaoPeriodica: React.FC<{
  session: AuthSession | null;
  onPronto: () => void;
  onAdiar: () => void;
}> = ({ session, onPronto, onAdiar }) => {
  const [score, setScore] = useState<number | null>(null);
  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9660, background: '#141110',
      display: 'flex', flexDirection: 'column', maxWidth: 460, margin: '0 auto',
      overflowY: 'auto', animation: 'e2FadeUp .32s ease both',
    }}>
      <div style={{ flex: 1, padding: 'calc(30px + env(safe-area-inset-top,0px)) 24px 24px' }}>
        {score === null
          ? <Srbai session={session} onAdiar={onAdiar} onPronto={setScore} />
          : (<>
            <div className="mono" style={{ ...eyebrow, color: 'rgba(245,241,234,0.45)' }}>Sua medida de hoje</div>
            <h2 style={{ fontFamily: serif, fontSize: 30, lineHeight: 1.1, color: CREME, margin: '10px 0 18px' }}>
              É isto que os números dizem.
            </h2>
            <Veredito score={score} />
            <button onClick={onPronto} style={{
              width: '100%', padding: '16px 20px', borderRadius: 999, border: 'none',
              background: 'var(--ember)', color: '#fff', fontSize: 15.5, fontWeight: 600, cursor: 'pointer',
            }}>Voltar</button>
          </>)}
      </div>
    </div>
  );
};
